import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Artikel, ArtikelSeite, Steuersatz,
              Unternehmen } from "@kundeninfo/api-client";
import { Objektliste, Objektzeile } from "@kundeninfo/ui";
import { ArtikelDialog } from "../../components/ArtikelDialog.js";
import { artikelMeta, quellenMarke } from "../../format.js";
import { renderMarkdown } from "../../markdown.js";
import { Meldung, SATZ_FORMAT, meldungAus, vorgabeUnternehmen } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich ARTIKELSTAMM (product, Paket 3b-1a).
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Der Katalog steht im INHALT — links seine
 * Liste mit Filterleiste und Blätterung, rechts der gewählte Artikel. Zwischen Etappe 2,
 * Aufgabe 3 und diesem Entscheid stand die Liste als zweite Ebene in der Verwaltungs-
 * Spalte; dort war sie neben dem Bereichs-Untermenü redundant, und ihre Zeilen brachen in
 * der schmalen Spalte um. Die Adressierung bleibt davon unberührt: Ein Klick auf eine
 * Zeile öffnet weiterhin `/verwaltung/artikel/<id>`.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * Der einzige Verwaltungsbereich mit **Filterleiste und Blätterung**. Alle anderen Bereiche
 * laden ihre Liste einmal und zeigen sie ganz; ein Artikelkatalog hat Tausende Zeilen und
 * wird deshalb serverseitig gefiltert und seitenweise geholt. Jede Änderung an einem Filter
 * setzt die Blätterung auf die erste Seite zurück — sonst stünde man nach dem Filtern auf
 * Seite 7 eines Ergebnisses mit einer Seite und sähe nichts.
 *
 * Die Steuersätze werden EINMAL geladen: als Auswahlliste im Dialog und um statt der
 * Kennung den Namen des Satzes zu zeigen. Der Artikel trägt bewusst nur die Kennung (siehe
 * openapi) — der Prozentsatz zum Belegdatum ist eine Frage an die Zeitreihe, nicht an den
 * Artikel.
 *
 * Wie bei Dateitypen und Steuersätzen: kein Versionsvergleich (last-write-wins), kein
 * Archivieren, sondern Stilllegen über einen eigenen Klick.
 *
 * Der Dialog selbst ist seit dem 04.09. die GETEILTE Komponente ArtikelDialog
 * (components/) — der Artikelkatalog öffnet für ADMINs denselben. Formularzustand,
 * Feldprüfung und 422-Anzeige wohnen dort; hier bleibt nur, WAS beim Speichern geschieht
 * (anlegen oder ändern) und dass danach neu gelesen wird. Aus demselben Grund gibt es hier
 * KEINEN Verlassen-Wächter: Ob im Dialog etwas Ungespeichertes steht, weiss allein er —
 * dieselbe Lage und dieselbe Entscheidung wie im Artikel-Bereich der Etappe 1.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste wie Detail
 * stehen im Inhalt. Der Spaltenblock `vws__` gehört seit dem 06.09.2026 wieder allein dem
 * Bereichs-Untermenü (Hausregel „CSS-Blocknamen je Ansicht getrennt").
 */

/** Nur die Wege dieses Bereichs — so lässt er sich im Test mit wenigen vi.fn zeichnen. */
export type ArtikelstammClient = Pick<ApiClient,
  "listProducts" | "createProduct" | "updateProduct" | "setProductActive" | "listTaxRates">;

/** Wie viele Artikel auf eine Seite gehen. Bewusst kleiner als die Servergrenze (200):
 *  Eine Seite soll ohne langes Scrollen überblickbar bleiben. */
const ARTIKEL_JE_SEITE = 20;

const PREIS_FORMAT = new Intl.NumberFormat("de-DE",
  { style: "currency", currency: "EUR" });

/** Wie bei den Vorreitern der Aufgabe 2 — siehe dort die ausführliche Begründung. */
export type VerwaltungArtikelProps = {
  aktiv: boolean;
  client: ArtikelstammClient;
  companies: Unternehmen[];
  /** Der geöffnete Artikel aus der Adresse (`/verwaltung/artikel/:id`). */
  artikelId: string | null;
  onOeffnen: (artikelId: string | null) => void;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7) — Vorgabe der
   *  Neuanlage, wenn kein Filter gesetzt ist. `null`: wie bisher das erste Unternehmen.
   *  Einen VORfilter bekommt der Artikelstamm nicht (Spec: nur Vorlagen, Textbausteine). */
  arbeitsunternehmenId: string | null;
};

/** Die ruhigen Marken einer Artikelzeile: „stillgelegt" und die Abrechnungsquelle — beide
 *  Eigenschaften, keine Zustände, deshalb Marken und kein Status-Chip. */
function artikelMarken(
  aktiv: boolean, quelle: "MONITOR" | "AIRCALL" | null,
): { marken?: string[] } {
  const marken = [
    ...(aktiv ? [] : ["stillgelegt"]),
    ...(quellenMarke(quelle) === null ? [] : [quellenMarke(quelle)!]),
  ];
  return marken.length === 0 ? {} : { marken };
}

export function useVerwaltungArtikel(p: VerwaltungArtikelProps): VerwaltungBereichTeile {
  const { aktiv, artikelId } = p;

  const [seite, setSeite] = useState<ArtikelSeite | null>(null);
  const [steuersaetze, setSteuersaetze] = useState<Steuersatz[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [suche, setSuche] = useState("");
  const [offset, setOffset] = useState(0);
  /** Der Artikel im geöffneten Dialog — { artikel: null } heisst: neu anlegen. */
  const [dialog, setDialog] = useState<{ artikel: Artikel | null } | null>(null);
  /** Die Kennung des zuletzt ANGELEGTEN Artikels — zwischen `onSpeichern` (wo sie
   *  ankommt) und `onGespeichert` (wo die Adresse gesetzt wird). Ein Ref und kein
   *  Zustand: Sie soll kein Neuzeichnen auslösen und wird noch im selben Durchlauf
   *  wieder verbraucht. */
  const angelegtId = useRef<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [schaltet, setSchaltet] = useState<string | null>(null);
  /** Wird hochgezählt, wenn eine Schreibaktion die Seite neu holen soll. */
  const [neuLaden, setNeuLaden] = useState(0);
  /**
   * Der aufgelöste Artikel zur Adresse. Er steht in EIGENEM Zustand und nicht als blosse
   * Suche in der Trefferseite (Muster: BereichArtikel der Etappe 1): Fällt die Zeile durch
   * einen Filterwechsel oder einen Seitenwechsel aus der Trefferseite heraus, bleibt der
   * zuvor aufgelöste Satz stehen — sonst risse ein Filterwechsel das Detail zu, das man
   * gerade liest.
   */
  const [satz, setSatz] = useState<Artikel | null>(null);

  const listProducts = p.client.listProducts;
  const listTaxRates = p.client.listTaxRates;

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listProducts({
      ...(companyId === "" ? {} : { companyId }),
      ...(kategorie === "" ? {} : { category: kategorie }),
      ...(suche === "" ? {} : { suche }),
      limit: ARTIKEL_JE_SEITE,
      offset,
    })
      .then((antwort) => {
        if (!abgebrochen) {
          setSeite(antwort);
          // Ein geglücktes Nachladen HEILT den alten Fehler (Lehre der Fixrunde 1 zu
          // Aufgabe 2) — der Hook lebt über der ganzen Anwendung.
          setLadeFehler(null);
        }
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Der Artikelstamm konnte nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listProducts, companyId, kategorie, suche, offset, neuLaden]);

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listTaxRates()
      .then((antwort) => { if (!abgebrochen) setSteuersaetze(antwort.items); })
      .catch(() => {
        // Ein Fehlschlag hier macht die Liste nicht unbrauchbar — nur die Auswahlliste im
        // Dialog bleibt leer, und statt des Namens steht dort der Rückfalltext.
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listTaxRates]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion): der offene
   * Dialog, die Sammelmeldung, der Ladefehler. Filter, Suchtext und Blätterung bleiben —
   * sie sind die Sicht auf die Liste, keine angefangene Eingabe (dieselbe Regel wie beim
   * Suchtext der Kundenliste, der einen Bereichswechsel ebenfalls überlebt).
   */
  useEffect(() => {
    if (aktiv) return;
    setDialog(null);
    setMeldung(null);
    setLadeFehler(null);
  }, [aktiv]);

  /*
   * Den Artikel zur Adresse auflösen — bevorzugt aus der frischen Trefferseite, sonst bleibt
   * der zuvor aufgelöste stehen (siehe die Notiz an `satz`). Ohne Adresse gibt es keinen.
   */
  useEffect(() => {
    if (artikelId === null) { setSatz(null); return; }
    const zeile = seite?.items.find((a) => a.id === artikelId);
    setSatz((alt) => {
      if (zeile !== undefined) return zeile;
      if (alt !== null && alt.id === artikelId) return alt;
      return null;
    });
  }, [artikelId, seite]);

  /* Der Änderungsdialog gehört DEM Artikel, über dem er geöffnet wurde — er fällt mit der
     ADRESSE, nicht mit dem Klick, der ihn sonst allein geschlossen hätte (Zurück-Taste,
     Rückweg im Kopf, ein Lesezeichen auf einen anderen Artikel). Der ANLEGE-Dialog
     (`artikel === null`) bleibt: Er gehört keinem Objekt, sondern dem Bereich, und steht
     bei der Liste. Die Sammelmeldung fällt mit ihm. */
  useEffect(() => {
    setDialog((d) =>
      (d === null || d.artikel === null || d.artikel.id === artikelId ? d : null));
    setMeldung(null);
  }, [artikelId]);

  /** Nur aktive Zeitscheiben stehen als Vorgabe zur Auswahl — ein stillgelegter Satz soll
   *  sich nicht neu vergeben lassen (er bleibt an bestehenden Artikeln stehen). */
  const waehlbareSaetze = steuersaetze.filter((s) => s.active);

  function satzName(id: string): string {
    const s = steuersaetze.find((x) => x.id === id);
    return s === undefined ? "unbekannter Steuersatz"
      : `${s.name} (${SATZ_FORMAT.format(s.ratePercent)} %)`;
  }

  /** Filter ändern heisst: zurück auf die erste Seite (siehe Kopf). */
  function filtern(setzen: () => void): void {
    setzen();
    setOffset(0);
  }

  function oeffnen(a: Artikel | null): void {
    setMeldung(null);
    setDialog({ artikel: a });
  }

  async function umschalten(a: Artikel): Promise<void> {
    setMeldung(null);
    setSchaltet(a.id);
    try {
      const neu = await p.client.setProductActive(a.id, !a.active);
      setSeite((alt) => (alt === null ? alt
        : { ...alt, items: alt.items.map((x) => (x.id === neu.id ? neu : x)) }));
      // Auch den aufgelösten Satz mitziehen: Er kann aus einer früheren Trefferseite
      // stammen und stünde sonst weiter mit dem alten Zustand da.
      setSatz((alt) => (alt !== null && alt.id === neu.id ? neu : alt));
    } catch (fehler) {
      setMeldung(meldungAus(fehler, "Der Zustand konnte nicht geändert werden."));
    } finally {
      setSchaltet(null);
    }
  }

  /** Der Dialograhmen — für Anlegen (bei der Liste) und Ändern (im Detail) derselbe. */
  function dialogRahmen(offen: { artikel: Artikel | null }): ReactElement {
    return (
      <ArtikelDialog
        /* Eigene Identität je Artikel: Der Dialog führt seinen Formularzustand selbst.
           Ohne diesen Schlüssel nähme ein Wechsel die Eingaben auf den nächsten mit. */
        key={offen.artikel?.id ?? "neu"}
        artikel={offen.artikel}
        companies={p.companies}
        steuersaetze={steuersaetze}
        vorgabe={{
          // Vorbelegt mit dem gefilterten Unternehmen — wer den Katalog einer Firma
          // ansieht, legt fast immer für dieselbe an. Ohne Filter das Arbeitsunternehmen.
          companyId: companyId !== "" ? companyId
            : vorgabeUnternehmen(p.arbeitsunternehmenId, p.companies),
          taxRateId: waehlbareSaetze[0]?.id ?? "",
        }}
        onSpeichern={async ({ id, companyId: firma, felder }) => {
          if (id === null) {
            // Die Kennung des frisch Angelegten durch die Naht des geteilten Dialogs
            // tragen: Er meldet Erfolg (`onGespeichert`) ohne Antwortsatz — den Vertrag
            // dafür zu erweitern, verlangte auch den Artikel-BEREICH umzubauen, der
            // denselben Dialog zeichnet und die Kennung nicht braucht.
            angelegtId.current = (
              await p.client.createProduct({ companyId: firma, ...felder })).id;
          } else {
            angelegtId.current = null;
            await p.client.updateProduct(id, felder);
          }
        }}
        onGespeichert={() => {
          // In beiden Fällen die Seite neu holen: Ein geänderter Name verschiebt die
          // Zeile innerhalb der Ordnung, ein neuer Artikel landet irgendwo im Katalog —
          // und die Gesamtzahl ändert sich. Beides weiss nur der Server.
          setDialog(null);
          setNeuLaden((n) => n + 1);
          // Und nach dem ANLEGEN geht die Adresse auf den frischen Satz: Wer eben etwas
          // angelegt hat, will es sehen. Beim Ändern bleibt sie, wo sie ist.
          const neu = angelegtId.current;
          angelegtId.current = null;
          if (neu !== null) p.onOeffnen(neu);
        }}
        onAbbrechen={() => setDialog(null)}
      />
    );
  }

  const von = seite === null || seite.total === 0 ? 0 : offset + 1;
  const bis = seite === null ? 0 : Math.min(offset + seite.items.length, seite.total);

  /* Wie im Bestand: Die Kategorienliste kommt vom Server und hängt NICHT am
     Kategoriefilter — sonst enthielte sie nach der ersten Wahl nur noch sich selbst. Eine
     gewählte, aber (etwa nach Unternehmenswechsel) nicht mehr angebotene Kategorie bleibt
     trotzdem drin, damit die Auswahl nicht still umspringt. */
  const kategorien = seite === null ? []
    : seite.categories.includes(kategorie) || kategorie === ""
      ? seite.categories : [...seite.categories, kategorie];

  // ---------------------------------------------------------------------------
  // Die Objektliste — linke Spalte des Inhalts
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      {/*
        * Der Anlege-Knopf und die Objektzeilen sind — anders als in den anderen
        * Objekt-Bereichen — NICHT gesperrt, solange ein Panel offen ist. Dieselbe
        * Entscheidung und dieselbe Begründung wie im Artikel-Bereich der Etappe 1: Der
        * ArtikelDialog ist MODAL und seit dem 06.09.2026 STRIKT — sein Hintergrund fängt
        * jeden Klick daneben ab und lässt ihn ins Leere laufen —, die Liste ist
        * währenddessen also gar nicht bedienbar; und der Dialog arbeitet ohne
        * Versionsvergleich (last-write-wins), es gibt also keine optimistische Sperre, die
        * ein Neuladen aushebeln könnte. Eine zusätzliche Sperre wäre hier nur eine
        * Behauptung ohne Wirkung.
        */}
      <div className="vw__leiste">
        <button type="button" className="vw__primaer" onClick={() => oeffnen(null)}>
          Neuer Artikel
        </button>
      </div>

      <div className="vw__filter" role="search">
        <div className="vw__auswahl">
          <label className="vw__label" htmlFor="vw-a-firma">Unternehmen</label>
          <select id="vw-a-firma" className="vw__select" value={companyId}
            onChange={(e) => filtern(() => setCompanyId(e.target.value))}>
            <option value="">alle</option>
            {p.companies.map((c) => (
              <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
            ))}
          </select>
        </div>
        <div className="vw__auswahl">
          <label className="vw__label" htmlFor="vw-a-kategorie">Kategorie</label>
          <select id="vw-a-kategorie" className="vw__select" value={kategorie}
            onChange={(e) => filtern(() => setKategorie(e.target.value))}>
            <option value="">alle</option>
            {kategorien.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>
        <div className="vw__auswahl">
          <label className="vw__label" htmlFor="vw-a-suche">Suche</label>
          <input id="vw-a-suche" className="vw__eingabe" type="search" value={suche}
            placeholder="Name oder Artikelnummer"
            onChange={(e) => filtern(() => setSuche(e.target.value))} />
        </div>
      </div>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && seite === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && seite !== null && seite.items.length === 0 && (
        <p className="vw__hinweis">Zu diesen Filtern gibt es keinen Artikel.</p>
      )}

      {/*
        Die Artikelzeilen (Paket „Objektzeilen nach ME-Design", 23.09.2026). Sie tragen den
        gemeinsamen Baustein aus @kundeninfo/ui — bis dahin zeichnete dieser Bereich seine
        Zeile selbst (`vw__objekt`), und sie sah anders aus als die des Artikel-Bereichs
        unter `/artikel`, obwohl beide denselben Artikel zeigen.

        ZWEI Änderungen am Inhalt, beide aus Dirks Befund: Die Zeile nennt jetzt IMMER Preis
        UND Kategorie (`artikelMeta`), und der Beschreibungs-Anriss ist ERSATZLOS entfallen
        („Dafür kann der Beschreibungstext bei Bedarf kürzer werden oder entfallen"). Die
        Beschreibung steht vollständig im Lese-Detail rechts — sie stand hier ohnehin nur
        gekürzt und ausserhalb des Knopfes, weil `renderMarkdown` Blockelemente liefert.
        Damit ist die Bestandsregel „drei Zeilen Anriss" für die ZEILEN aufgehoben;
        `kn-md-anriss` bleibt für andere Stellen bestehen.
      */}
      <Objektliste label="Artikelstamm">
        {(seite?.items ?? []).map((a) => (
          <Objektzeile
            key={a.id}
            name={a.name}
            kennung={a.productNumber}
            meta={artikelMeta(a.netUnitPrice, a.unit, a.category)}
            /* „stillgelegt" ist eine Eigenschaft und kein Zustandsverlauf — deshalb eine
               ruhige Marke und kein Status-Chip. Der Artikelstamm ist die einzige
               Artikelliste, die stillgelegte Sätze überhaupt zeigt (der Leseweg
               `GET /api/artikel` gibt nur aktive heraus). */
            {...artikelMarken(a.active, a.billingDataSource)}
            gewaehlt={a.id === artikelId}
            /* Der zugängliche Name nennt Nummer UND Namen; zwanzig Zeilen „Öffnen" wären
               für jemanden, der die Seite hört, nicht unterscheidbar. */
            ariaLabel={`Artikel ${a.productNumber}, ${a.name}, anzeigen`}
            onOeffnen={() => p.onOeffnen(a.id)}
          />
        ))}
      </Objektliste>

      {/* Die Blätterung steht auch bei einem leeren Ergebnis da (Bestandsverhalten): „0–0
          von 0" ist die Antwort auf die Frage, wie viele es sind — sie zu verstecken hiesse
          die Auskunft gerade dann wegzunehmen, wenn man sie braucht. */}
      {seite !== null && (
        <div className="vw__blaettern">
          <button type="button" className="vw__knopf" disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - ARTIKEL_JE_SEITE))}>
            Zurück
          </button>
          <span className="vw__blaettern-stand">{von}–{bis} von {seite.total}</span>
          <button type="button" className="vw__knopf"
            disabled={offset + seite.items.length >= seite.total}
            onClick={() => setOffset((o) => o + ARTIKEL_JE_SEITE)}>
            Weiter
          </button>
        </div>
      )}

      {/* Der Anlege-Dialog steht bei seinem Knopf, also bei der Liste. */}
      {dialog !== null && dialog.artikel === null && dialogRahmen(dialog)}
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — GENAU EIN Artikel
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (artikelId === null) {
      return (
        <p className="vw__hinweis">
          Links einen Artikel wählen — Beschreibung, Preis, Kategorie und Steuersatz stehen
          dann hier. Über der Liste stehen Filter und Suche des ganzen Katalogs.
        </p>
      );
    }
    if (satz === null) {
      /*
       * Die dreistufige Auskunft für ein Lesezeichen auf einen Artikel, der nicht in der
       * Trefferseite steht (Etappe-1-Lektion): „steht nicht in der Liste" darf erst
       * behauptet werden, wenn es eine Liste GIBT. Solange sie lädt, wäre die Aussage
       * falsch; ist sie gescheitert, liegt es nicht am Artikel.
       */
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (seite === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Dieser Artikel steht nicht in der Trefferliste — bitte über die Suche in der
          Liste öffnen.
        </p>
      );
    }

    const a = satz;
    return (
      /* Eigene Identität je Artikel (Etappe-1-Lektion `key={objekt.id}`). */
      <div key={a.id} className="vw__eintrag">
        {meldung !== null && dialog === null && <Meldung text={meldung} />}
        <div className="vw__eintrag-kopf">
          <span className="vw__eintrag-name">{a.name}</span>
          <span className="vw__marke">{a.productNumber}</span>
          {!a.active && <span className="vw__marke">stillgelegt</span>}
          {quellenMarke(a.billingDataSource) !== null && (
            <span className="vw__marke">{quellenMarke(a.billingDataSource)}</span>
          )}
        </div>
        {/* Die Beschreibung GERENDERT (Dirk-Entscheid 05.09.2026), wie im Artikelkatalog;
            `renderMarkdown` sanitisiert selbst und wird nie mit dem `unsicher`-Schalter
            gerufen. Im Detail ohne die Kürzung der Liste (`kn-md-anriss`): Hier ist Platz,
            und die ROHE Quelle steht im Änderungsdialog. Leer heisst: gar kein Absatz. */}
        {a.description !== null && a.description.trim() !== "" && (
          <div className="vw__eintrag-beschreibung">
            {renderMarkdown(a.description)}
          </div>
        )}
        <span className="vw__eintrag-wert">
          {PREIS_FORMAT.format(a.netUnitPrice)} netto je {a.unit}
        </span>
        <span className="vw__eintrag-zusatz">
          {a.category ?? "ohne Kategorie"} · {satzName(a.taxRateId)}
        </span>
        <div className="vw__eintrag-aktionen">
          <button type="button" className="vw__knopf"
            aria-label={`${a.productNumber} ändern`} onClick={() => oeffnen(a)}>
            Ändern
          </button>
          <button type="button" className="vw__knopf" disabled={schaltet === a.id}
            aria-label={a.active
              ? `${a.productNumber} stilllegen` : `${a.productNumber} wieder einschalten`}
            onClick={() => { void umschalten(a); }}>
            {a.active ? "Stilllegen" : "Wieder einschalten"}
          </button>
        </div>

        {/* Der Änderungsdialog steht bei seinem Knopf, also im Detail — und gehört GENAU
            DEM Artikel, über dem er geöffnet wurde (Lehre der Fixrunde 1 zu Aufgabe 2). */}
        {dialog !== null && dialog.artikel !== null && dialog.artikel.id === a.id
          && dialogRahmen(dialog)}
      </div>
    );
  })();

  return { liste, detail };
}
